import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';

function AutoReload({ url = '/', tryDelay = 60 * 60 * 1000 }) {
  const [codeHasChanged, setCodeHasChanged] = useState(false);
  const previousHashRef = useRef(null);
  const intervalRef = useRef(null);

  const hash = (str) => {
    const len = str.length;
    let hash = 0;
    if (len === 0) return hash;
    for (let i = 0; i < len; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  };

  const fetchSource = useCallback(() => {
    return fetch(url)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('offline');
        }
        return response.text();
      })
      .then((html) => {
        const currentHash = hash(html);
        if (!previousHashRef.current) {
          previousHashRef.current = currentHash;
          return;
        }
        if (previousHashRef.current !== currentHash) {
          previousHashRef.current = currentHash;
          setCodeHasChanged(true);
        }
      })
      .catch(() => {
        /* do nothing */
      });
  }, [url]);

  useEffect(() => {
    fetchSource();
    intervalRef.current = setInterval(fetchSource, tryDelay);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [tryDelay, fetchSource]);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Dialog
      open={codeHasChanged}
      onClose={handleReload}
      aria-labelledby='auto-reload-dialog-title'
    >
      <DialogTitle id='auto-reload-dialog-title'>
        <DownloadIcon sx={{ verticalAlign: 'middle', mr: 1 }} />A new version of
        the {import.meta.env.VITE_ORG_NAME} app is available.
      </DialogTitle>
      <DialogContent>
        <Typography>
          Click OK to update or refresh your browser window.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          color='primary'
          onClick={handleReload}
          startIcon={<CheckIcon />}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AutoReload.propTypes = {
  url: PropTypes.string,
  tryDelay: PropTypes.number,
};

export default AutoReload;
